import { createServerFn } from "@tanstack/react-start";
import { execSync } from "node:child_process";
import { join } from "node:path";
import { Octokit } from "octokit";
import { authMiddleware } from "./auth-middleware";
import type { PendingEdit } from "./cms-store";
import { IMAGE_WIDTHS, LARGEST_SUFFIX, pathWithWidth, stemFromLargest } from "./image-paths";

// Env vars for GitHub repo access
function getGitHubConfig() {
  const token = process.env.GITHUB_TOKEN;
  const repo = process.env.GITHUB_REPO; // e.g. "owner/repo"
  if (!token || !repo) {
    throw new Error("GITHUB_TOKEN and GITHUB_REPO must be set");
  }
  const [owner, name] = repo.split("/");
  return { token, owner, repo: name };
}

// Returns GitHub credentials for client-side blob uploads
// Safe because the token is a fine-grained PAT scoped to one repo with contents:write only
export const getGitHubUploadConfig = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    const config = getGitHubConfig();
    return { token: config.token, owner: config.owner, repo: config.repo };
  });

interface PublishInput {
  edits: Record<string, PendingEdit>;
  /** Map of (suffixed) image path to blob SHA (already uploaded). One entry per variant. */
  imageShas: Record<string, string>;
  /** Map of canonical largest-variant path → base64 LQIP data URL. */
  imageLqips?: Record<string, string>;
}


export const publishChanges = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .inputValidator((data: PublishInput) => data)
  .handler(async ({ data, context }) => {
    const { edits, imageShas, imageLqips = {} } = data;

    if (Object.keys(edits).length === 0 && Object.keys(imageShas).length === 0) {
      return { success: false as const, error: "No changes to publish" };
    }

    // For each edit's data fields, if a string value matches a known optimized
    // image path, also write the companion `${field}Lqip` field.
    function withLqipCompanions(record: Record<string, unknown>): Record<string, unknown> {
      const out: Record<string, unknown> = { ...record };
      for (const [key, value] of Object.entries(record)) {
        if (typeof value === "string" && imageLqips[value]) {
          out[`${key}Lqip`] = imageLqips[value];
        }
      }
      return out;
    }

    const config = getGitHubConfig();
    const octokit = new Octokit({ auth: config.token });
    const { owner, repo } = config;

    try {
      // 1. Get the current commit SHA for main
      const { data: ref } = await octokit.rest.git.getRef({
        owner,
        repo,
        ref: "heads/main",
      });
      const baseSha = ref.object.sha;

      // 2. Get the base tree
      const { data: baseCommit } = await octokit.rest.git.getCommit({
        owner,
        repo,
        commit_sha: baseSha,
      });
      const baseTreeSha = baseCommit.tree.sha;

      // 3. Build file changes by applying edits to current content
      const fileChanges: { path: string; content: string }[] = [];

      // Group edits by file
      const editsByFile = new Map<string, PendingEdit[]>();
      for (const edit of Object.values(edits)) {
        const existing = editsByFile.get(edit.file) ?? [];
        existing.push(edit);
        editsByFile.set(edit.file, existing);
      }

      // For each changed file, fetch current content and apply edits
      for (const [file, fileEdits] of editsByFile) {
        const filePath = `src/content/${file}.json`;

        // Fetch current file content from GitHub
        let content: unknown;
        try {
          const { data: fileData } = await octokit.rest.repos.getContent({
            owner,
            repo,
            path: filePath,
            ref: "main",
          });
          if ("content" in fileData) {
            content = JSON.parse(
              Buffer.from(fileData.content, "base64").toString("utf-8"),
            );
          }
        } catch {
          // File doesn't exist yet — start with empty array
          content = [];
        }

        // Apply each edit
        for (const edit of fileEdits) {
          const editData = withLqipCompanions(edit.data);
          if (Array.isArray(content)) {
            if (edit.type === "update" && edit.id) {
              const idx = content.findIndex(
                (item: { id?: string }) => item.id === edit.id,
              );
              if (idx !== -1) {
                content[idx] = { ...content[idx], ...editData };
              }
            } else if (edit.type === "add") {
              const exists = content.some(
                (item: { id?: string }) => item.id === edit.id,
              );
              if (!exists) {
                content.push(editData);
              }
            } else if (edit.type === "delete" && edit.id) {
              content = (content as { id?: string }[]).filter(
                (item) => item.id !== edit.id,
              );
            } else if (edit.type === "reorder") {
              const orderedIds = (edit.data?.orderedIds as string[] | undefined) ?? [];
              if (orderedIds.length > 0) {
                const arr = content as { id?: string }[];
                const byId = new Map(arr.map((item) => [item.id, item] as const));
                const reordered: typeof arr = [];
                for (const id of orderedIds) {
                  const item = byId.get(id);
                  if (item) {
                    reordered.push(item);
                    byId.delete(id);
                  }
                }
                for (const remaining of byId.values()) reordered.push(remaining);
                content = reordered;
              }
            }
          } else if (edit.type === "update") {
            content = { ...(content as Record<string, unknown>), ...editData };
          }
        }

        fileChanges.push({
          path: filePath,
          content: JSON.stringify(content, null, 2) + "\n",
        });
      }

      // 4. Create tree with all file changes
      const treeItems: {
        path: string;
        mode: "100644";
        type: "blob";
        content?: string;
        sha?: string;
      }[] = fileChanges.map((file) => ({
        path: file.path,
        mode: "100644" as const,
        type: "blob" as const,
        content: file.content,
      }));

      // Add image blobs (already uploaded, just reference by SHA)
      for (const [imagePath, sha] of Object.entries(imageShas)) {
        treeItems.push({
          path: `public${imagePath}`,
          mode: "100644",
          type: "blob",
          sha,
        });
      }

      const { data: newTree } = await octokit.rest.git.createTree({
        owner,
        repo,
        base_tree: baseTreeSha,
        tree: treeItems,
      });

      // 5. Build commit message
      const changedFiles = [...editsByFile.keys()];
      const imageCount = Object.keys(imageShas).length;
      const parts: string[] = [];
      if (changedFiles.length) parts.push(changedFiles.join(", "));
      if (imageCount) parts.push(`${imageCount} image(s)`);
      const message = `CMS update: ${parts.join("; ")}`;

      // 6. Create commit
      const { data: newCommit } = await octokit.rest.git.createCommit({
        owner,
        repo,
        message,
        tree: newTree.sha,
        parents: [baseSha],
        author: {
          name: `CMS (${context.user.githubUser})`,
          email: "butler.borst@gmail.com",
          date: new Date().toISOString(),
        },
      });

      // 7. Update main ref
      await octokit.rest.git.updateRef({
        owner,
        repo,
        ref: "heads/main",
        sha: newCommit.sha,
      });

      // In dev, sync just the changed files (avoids conflicts with unrelated local changes)
      try {
        const cwd = process.cwd();
        execSync("git fetch origin main", { cwd, encoding: "utf-8", timeout: 15_000 });
        const changedPaths = [
          ...fileChanges.map((f) => f.path),
          ...Object.keys(imageShas).map((p) => `public${p}`),
        ];
        for (const p of changedPaths) {
          try {
            execSync(`git checkout origin/main -- "${p}"`, { cwd, encoding: "utf-8", timeout: 5_000 });
          } catch {
            // Individual file checkout failed — not critical
          }
        }
      } catch {
        // Not critical — only matters in local dev
      }

      return { success: true as const, message, sha: newCommit.sha };
    } catch (err) {
      console.error("Publish failed:", err);
      return {
        success: false as const,
        error: "Publish failed. Please try again.",
      };
    }
  });

export const listRepoImages = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    const IMAGE_RE = /\.(jpg|jpeg|png|gif|svg|webp|avif)$/i;
    const results: string[] = [];

    // In local dev, prefer the working filesystem so uncommitted changes
    // (e.g. a backfill that hasn't been pushed yet) show up immediately.
    if (process.env.NODE_ENV === "development") {
      const { readdirSync, statSync } = await import("node:fs");
      const root = join(process.cwd(), "public/images");
      function walkLocal(absDir: string) {
        let entries: string[] = [];
        try {
          entries = readdirSync(absDir);
        } catch {
          return;
        }
        for (const entry of entries) {
          const full = join(absDir, entry);
          const st = statSync(full);
          if (st.isDirectory()) {
            walkLocal(full);
          } else if (IMAGE_RE.test(entry)) {
            const rel = "/" + full.substring(join(process.cwd(), "public").length + 1);
            results.push(rel);
          }
        }
      }
      walkLocal(root);
      return results.sort();
    }

    const config = getGitHubConfig();
    const octokit = new Octokit({ auth: config.token });
    const { owner, repo } = config;

    async function walk(dir: string) {
      try {
        const { data } = await octokit.rest.repos.getContent({
          owner,
          repo,
          path: dir,
          ref: "main",
        });
        if (!Array.isArray(data)) return;
        for (const item of data) {
          if (item.type === "file" && IMAGE_RE.test(item.name)) {
            // Convert "public/images/foo.jpg" to "/images/foo.jpg"
            results.push("/" + item.path.replace(/^public\//, ""));
          } else if (item.type === "dir") {
            await walk(item.path);
          }
        }
      } catch {
        // Directory doesn't exist — that's fine
      }
    }

    await walk("public/images");
    return results.sort();
  });

export const deleteRepoImage = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .inputValidator((data: { path: string }) => data)
  .handler(async ({ data: { path }, context }) => {
    const config = getGitHubConfig();
    const octokit = new Octokit({ auth: config.token });
    const { owner, repo } = config;

    // For optimized paths, delete all 4 sibling variants in a single tree commit.
    // For legacy single-file paths, fall back to single-file delete.
    const optimized = path.endsWith(LARGEST_SUFFIX);
    const targetPaths = optimized
      ? IMAGE_WIDTHS.map((w) => pathWithWidth(stemFromLargest(path), w))
      : [path];

    try {
      if (optimized) {
        // Multi-file delete via tree manipulation
        const { data: ref } = await octokit.rest.git.getRef({
          owner,
          repo,
          ref: "heads/main",
        });
        const baseSha = ref.object.sha;
        const { data: baseCommit } = await octokit.rest.git.getCommit({
          owner,
          repo,
          commit_sha: baseSha,
        });

        const treeItems = targetPaths.map((p) => ({
          path: `public${p}`,
          mode: "100644" as const,
          type: "blob" as const,
          sha: null as unknown as string,
        }));

        const { data: newTree } = await octokit.rest.git.createTree({
          owner,
          repo,
          base_tree: baseCommit.tree.sha,
          tree: treeItems,
        });

        const { data: newCommit } = await octokit.rest.git.createCommit({
          owner,
          repo,
          message: `CMS delete: ${path.split("/").pop()} (4 variants)`,
          tree: newTree.sha,
          parents: [baseSha],
          committer: {
            name: `CMS (${context.user.githubUser})`,
            email: "butler.borst@gmail.com",
          },
        });

        await octokit.rest.git.updateRef({
          owner,
          repo,
          ref: "heads/main",
          sha: newCommit.sha,
        });
      } else {
        const filePath = `public${path}`;
        const { data: fileData } = await octokit.rest.repos.getContent({
          owner,
          repo,
          path: filePath,
          ref: "main",
        });
        if (Array.isArray(fileData) || fileData.type !== "file") {
          return { success: false, error: "Not a file" };
        }
        await octokit.rest.repos.deleteFile({
          owner,
          repo,
          path: filePath,
          message: `CMS delete: ${path.split("/").pop()}`,
          sha: fileData.sha,
          committer: {
            name: `CMS (${context.user.githubUser})`,
            email: "butler.borst@gmail.com",
          },
        });
      }

      // Remove local files in dev
      try {
        const { unlinkSync } = await import("node:fs");
        for (const p of targetPaths) {
          try {
            unlinkSync(join(process.cwd(), `public${p}`));
          } catch {
            // sibling not present locally — fine
          }
        }
      } catch {
        // Not critical
      }

      return { success: true };
    } catch (err) {
      console.error("Delete image failed:", err);
      return { success: false, error: "Delete failed" };
    }
  });

export const renameRepoImage = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .inputValidator((data: { oldPath: string; newPath: string }) => data)
  .handler(async ({ data: { oldPath, newPath }, context }) => {
    const config = getGitHubConfig();
    const octokit = new Octokit({ auth: config.token });
    const { owner, repo } = config;

    // For optimized paths, rename all 4 sibling variants together so the
    // -1800 / -1200 / -800 / -400 set stays in sync.
    const optimized = oldPath.endsWith(LARGEST_SUFFIX) && newPath.endsWith(LARGEST_SUFFIX);
    const pairs = optimized
      ? IMAGE_WIDTHS.map((w) => ({
          old: pathWithWidth(stemFromLargest(oldPath), w),
          next: pathWithWidth(stemFromLargest(newPath), w),
        }))
      : [{ old: oldPath, next: newPath }];

    try {
      // Look up the blob SHA for each existing source file.
      const pairsWithSha: { old: string; next: string; sha: string }[] = [];
      for (const pair of pairs) {
        try {
          const { data: fileData } = await octokit.rest.repos.getContent({
            owner,
            repo,
            path: `public${pair.old}`,
            ref: "main",
          });
          if (!Array.isArray(fileData) && fileData.type === "file") {
            pairsWithSha.push({ ...pair, sha: fileData.sha });
          }
        } catch {
          // Sibling missing — skip
        }
      }
      if (pairsWithSha.length === 0) {
        return { success: false, error: "No source files found" };
      }

      const { data: ref } = await octokit.rest.git.getRef({
        owner,
        repo,
        ref: "heads/main",
      });
      const baseSha = ref.object.sha;
      const { data: baseCommit } = await octokit.rest.git.getCommit({
        owner,
        repo,
        commit_sha: baseSha,
      });

      // Tree ops: create each new path with the existing blob SHA, delete each old path.
      const treeItems: {
        path: string;
        mode: "100644";
        type: "blob";
        sha: string;
      }[] = [];
      for (const p of pairsWithSha) {
        treeItems.push({ path: `public${p.next}`, mode: "100644", type: "blob", sha: p.sha });
        treeItems.push({
          path: `public${p.old}`,
          mode: "100644",
          type: "blob",
          sha: null as unknown as string,
        });
      }

      const { data: newTree } = await octokit.rest.git.createTree({
        owner,
        repo,
        base_tree: baseCommit.tree.sha,
        tree: treeItems,
      });

      const oldName = oldPath.split("/").pop();
      const newName = newPath.split("/").pop();
      const variantSuffix = optimized ? ` (${pairsWithSha.length} variants)` : "";
      const { data: newCommit } = await octokit.rest.git.createCommit({
        owner,
        repo,
        message: `CMS rename: ${oldName} → ${newName}${variantSuffix}`,
        tree: newTree.sha,
        parents: [baseSha],
        committer: {
          name: `CMS (${context.user.githubUser})`,
          email: "butler.borst@gmail.com",
        },
      });

      await octokit.rest.git.updateRef({
        owner,
        repo,
        ref: "heads/main",
        sha: newCommit.sha,
      });

      // Sync locally in dev: fetch + checkout new files, remove old
      try {
        const cwd = process.cwd();
        execSync("git fetch origin main", { cwd, encoding: "utf-8", timeout: 15_000 });
        const { unlinkSync } = await import("node:fs");
        for (const p of pairsWithSha) {
          try {
            execSync(`git checkout origin/main -- "public${p.next}"`, {
              cwd,
              encoding: "utf-8",
              timeout: 5_000,
            });
          } catch {
            // not critical
          }
          try {
            unlinkSync(join(cwd, `public${p.old}`));
          } catch {
            // already gone
          }
        }
      } catch {
        // Not critical
      }

      return { success: true };
    } catch (err) {
      console.error("Rename image failed:", err);
      return { success: false, error: "Rename failed" };
    }
  });
